import React from 'react';

import { Input, Text, ButtonGroup, ImageGroup, ImageBoxGroup, FormulationGroup, ColourGroup } from '../components/Form';
import {ColourScreen, FormulationScreen} from '../components/Screens';

import { goalValidator } from './validators';

// The quiz steps, questions and options.
export default [
  {
    name: 'Your Details',
    steps: [
      {
        heading: "Disclaimer",
        subHeading: () => (
          <>
            <p>This product is meant for general use only and is not intended to diagnose, cure, treat or prevent any disease. None of the information on this website has been evaluated by the Therapeutic Goods Administration (TGA), the Food and Drugs Administration (FDA), the European Medicines Agency (EMA) or any other regulatory body.</p>
            <p>Whilst you may opt for a stimulant-free formula, this product can contain caffeine, in which cases it isn’t suitable for children, pregnant or lactating women, and individuals sensitive to caffeine. Consume no more than the recommended dosage. It’s important to remember that people can respond uniquely to different ingredients, and some of the ingredients in this product may interact with other medications or supplements you're using. In some cases this could result in adverse effects.</p>
            <p>If you have questions about any of this, we recommend discussing it with your doctor as they are best equipped to help you make an informed decision. You may also contact us. Whilst we are not able to provide specific medical advice, we are always happy to help however else we can.</p>
          </>
        ),
        autoSubmit: true,
        field: {
          component: ButtonGroup,
          options: [{ value: 'I understand' }],
          cols: 2,
          extraClass: 'disclaimer',
          rules: ['required'],
          optionalLink:{ text: 'Take me back', link: '/' }
        }
      },
      {
        heading: 'Choose how you’d like to formulate your pre-workout.',
        autoSubmit: true,
        field: {
          component: ButtonGroup,
          options: [
            {
              value: 'Quiz me',
              description: "We\'ll use your answers to recommend a formula that we think will work well for you. You can still make changes to your formula at the end if you’d like.",
              highlight: 'Recommended',
            },
            {
              value: 'I\'ll do it myself',
              description: "Add or remove ingredients and adjust dosages. If you’re knowledgeable and experienced, and know exactly what you want, this may work better for you.",
              highlight: 'Completely customised',
            },
          ],
          cols: 2,
          extraClass: 'featured',
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>You may wish to take the quiz to see our recommendations for your formula your pre-workout formula. These are recommendations and you can make adjustments at the end of the quiz.</p>
              <p>If you know what you like, you can select <i>I'll do it myself</i> to bypass most of the quiz questions and edit your formula directly.</p>
            </>
          ),
        },
      },
      {
        heading: 'What should we call you?',
        autoSubmit: false,
        field: {
          component: Input,
          label: 'Enter your first name...',
          rules: ['required', 'alpha'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <p>
              Now that we're getting to know each other, we'd like to know what we should call you. If you'd prefer, you
              can enter a nickname or pseudonym. This name will be printed on your order information leaflet in your final order.
            </p>
          ),
        },
      },
      {
        heading: 'Nice to meet you, {{ whatShouldWeCallYou }}! How old are you?',
        field: {
          name: 'How old are you?',
          component: ButtonGroup,
          options: [
            { value: 'Under 20' },
            { value: 'In my 20s' },
            { value: 'In my 30s' },
            { value: 'In my 40s' },
            { value: 'In my 50s' },
            { value: 'In my 60s' },
            { value: '70 or over' },
            { value: 'Prefer not to say' },
          ],
          cols: 2,
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Certain age demographics may have a higher likelihood of experiencing adverse effects due to factors specific to their individual context, such as decreased liver or kidney function and an increased likelihood of using other medications. We utilise this information to provide you with relevant advice, whether specific or general, to help you make well-informed decisions. We recommend consulting with a healthcare professional for further guidance.</p>
              <p>We also use this information to understand who our customers are and what we can do to make our products better for them.</p>
              <p>Don't feel like sharing? That's totally fine (we get it)! You can skip the question by selecting the <i>Prefer not to say</i> option.</p>
            </>
          ),
        },
      },
      {
        heading: "What's your sex?",
        field: {
          component: ButtonGroup,
          options: [{ value: 'Male' }, { value: 'Female' }, { value: 'Prefer not to say' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Males and females may respond differently to the same ingredients, and these differences can be independent of body weight variations. Some research, especially older studies, has been limited to male participants only. When we have limited data concerning gender-specific responses to certain ingredients, we'll highlight this to help you make well-informed decisions. These variances might arise from genetic factors or lifestyle differences, such as the use of combined oral contraceptives. Additionally, if there's any potential concern related to pregnancy, we will provide further information, recommending you consult with a healthcare professional for guidance.</p>
              <p>We also use this information to understand who our customers are and what we can do to make our products better for them.</p>
              <p>Don't feel like sharing? That's totally fine (we get it)! You can skip the question by selecting the <i>Prefer not to say</i> option.</p>
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Exercise & Goals',
    steps: [
      {
        heading: 'What types of exercise will you use your pre-workout for?',
        subHeading: 'You can select more than one.',
        autoSubmit: false,
        field: {
          component: ImageGroup,
          options: [
            { value: 'Resistance', svg: 'resistance', description: 'Weightlifting, bodyweight exercises, etc.' },
            { value: 'Cardio', svg: 'cardio', description: "Running, cycling, swimming, rowing, etc." },
            { value: 'Flexibility and balance', svg: 'flexibility-balance', description: 'Stretching, yoga, tai chi, etc.' },
            { value: 'Sports', svg: 'sports', description: 'Football, soccer, basketball, tennis, etc.' },
          ],
          multi: true,
          extraClass: 'box-img-gap',
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>
                Different ingredients are found to be more beneficial for certain types of exercises compared to others. We leverage this knowledge to suggest optional ingredients, basing our recommendations on the research we've reviewed and in conjunction with your responses from the quiz.
              </p>
            </>
          ),
        },
      },
      {
        heading: "How often will you use your pre-workout?",
        field: {
          component: ButtonGroup,
          options: [{ value: 'Here and there' }, { value: 'Once or twice a week' }, { value: 'A few times a week' }, { value: 'More days than not' }, { value: 'Every day' }, { value: 'More than once a day' }],
          cols: 2,
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>The effects of ingredients can vary based on usage patterns. We utilize this information to tailor recommendations for you, considering your other quiz responses. Additionally, this insight helps us comprehend how our customers use our products, enabling us to enhance them for a better user experience.</p>
            </>
          ),
        },
      },
      {
        heading: 'What are your current weight goals?',
        field: {
          component: ButtonGroup,
          options: [{ value: 'Lose weight' }, { value: 'Maintain weight' }, { value: 'Gain weight' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Certain ingredients are identified to be more beneficial for specific results compared to others. We use this knowledge to suggest optional ingredients, basing our recommendations on the research we've examined and your other quiz responses. This insight also aids us in understanding our customers' needs, enabling us to create improved products for them.</p>
            </>
          ),
        },
      },
      {
        heading: 'Which of the following outcomes are you most looking for from your pre-workout?',
        field: {
          component: ButtonGroup,
          options: [{ value: 'More energy and motivation' }, { value: 'Increased strength' }, { value: 'Increased endurance' }, { value: 'Improved pumps' }, { value: 'Increased muscle gain' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>We adjust ingredient choices based on your primary goal to ensure they align with the benefits certain ingredients may offer. Recognising that many people have multiple primary goals, you can modify these recommendations later if you desire a different emphasis. This feedback also enables us to understand our customers' needs and preferences, helping us to design superior products.</p>
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Stimulants',
    steps: [
      {
        heading: 'Do you want a stimulant-containing pre-workout?',
        field: {
          component: ButtonGroup,
          options: [{ value: 'Yes' }, { value: 'No' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Our pre-workout contains caffeine as its stimulant. If you choose <i>yes</i>, we will pose further questions to determine the recommended caffeine dosage per serving for you. Some individuals prefer non-stimulant alternatives for various reasons. If you select <i>no</i>, we will skip all caffeine-related questions, and the final product won't include caffeine.</p>
            </>
          ),
        },
      },
      {
        heading: 'How much do you weigh?',
        autoSubmit: false,
        field: {
          component: Input,
          label: 'Enter your weight in kilograms',
          rules: ['required', 'number'],
          type: 'number',
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Certain ingredients have recommended dosages based on body weight. We use your body weight as a starting point to calculate the suggested per-serving quantities, grounded in research-backed dosages. Presently, this approach is applied specifically for caffeine.</p>
            </>
          ),
        },
      },
      {
        heading: 'Do you have much experience with caffeine-containing pre-workout products?',
        field: {
          component: ButtonGroup,
          options: [{ value: 'None' }, { value: 'A little bit' }, { value: 'Plenty' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Your experience with caffeine-containing pre-workouts helps determine an appropriate starting dosage per serving, taking into account your other responses. We advise you to answer as accurately as possible. You can adjust your desired dose on the formulation page.</p>
              <p>Interested in more details? Side effects from caffeine are dose-dependent. If you're inexperienced with caffeine, a lower set point is generally safer. Your final recommendation is also influenced by answers to other questions about your situation. We use this data to tailor the suggested dose and offer relevant advice.</p>
            </>
          ),
        },
      },
      {
        heading: 'How often do you consume caffeine?',
        subHeading: 'Think coffee, tea, energy drinks, pre-workouts, caffeine tablets, soft drinks, and so on.',
        field: {
          component: ButtonGroup,
          options: [{ value: 'Almost never' }, { value: 'A bit here and there' }, { value: 'Once or twice a day' }, { value: 'Several times a day' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>Regular caffeine consumption can lead to increased tolerance to some of its effects. Based on your consumption rate, we can gauge potential adjustments to your dosage in relation to your tolerance level. This information helps us refine the recommended dose and offer advice.</p>
              <p>Exercise caution with your caffeine intake, especially when consuming multiple caffeine-containing products in a single day. Caffeine metabolism varies greatly among individuals. Tolerance doesn't uniformly affect all outcomes. Some side effects might not develop tolerance in the same way as the desired effects. Upping the dose might not necessarily amplify the benefits; it could merely intensify the side effects and prolong their duration.</p>
            </>
          ),
        },
      },
      {
        heading: 'Do you experience negative side effects when using caffeine?',
        subHeading: 'This includes anxiety, insomnia, digestive issues, rapid heart rate, and so on.',
        field: {
          component: ButtonGroup,
          options: [{ value: 'Never' }, { value: 'Sometimes' }, { value: 'Often' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
              <p>We're interested in understanding how you typically react to a standard dose of caffeine (100-200 mg). People often have varied responses to similar caffeine dosages, regardless of body weight. Given that you indicated wanting a formula containing stimulants, knowing how you handle this dosage helps us gauge your compatibility with our suggested dose. If you encounter side effects, your dose might be modified based on your other responses, or we might suggest incorporating another ingredient to mitigate these effects.</p>
            </>
          ),
        },
      },
      {
        heading: 'Will you use your pre-workout close to bedtime?',
        field: {
          component: ButtonGroup,
          options: [{ value: 'Never' }, { value: 'Sometimes' }, { value: 'Often' }],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Why do we ask?',
          body: (
            <>
               <p>If you frequently take your pre-workout close to bedtime, we'll suggest a reduced dosage. However, you can fine-tune this later on the formulation page. We advise against consuming caffeine near sleep times.</p>
               <p>While you might feel that caffeine doesn't impact your sleep, its interference with sleep quality is well established.</p>
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Formulation',
    steps: [
      {
        heading: 'Your formulation',
        subHeading: 'Here’s a starting point for your pre-workout formula. You can adjust it if you’d like.',
        screen: FormulationScreen,
        autoSubmit: false,
        field: {
          component: FormulationGroup,
        },
      },
      {
        heading: 'Choose a flavour for your pre-workout.',
        field: {
          component: ImageBoxGroup,
          options: [
            { value: 'No flavouring', svg: 'no-flavour', description: 'Pre-workout ingredients have a very strong taste that most find unpleasant. We only recommend this option if you plan to mix your pre-workout with something else.', imgClass: 'small-icon' },
            { value: 'Pineapple', png: 'pineapple', description: 'A mix of the subtle pineapple flavour notes similar to a juice with the acidic citrus taste.' },
            { value: 'Blackcurrant', png: 'blackcurrent', description: 'The unmistakable flavour of blackcurrant is very similar to that seen in ice blocks.' },
            { value: 'Tropical', png: 'tropical', description: 'Mixed sweet tropical esesences paired with an underlying citrus taste.' },
            { value: 'Mango', png: 'mango', description: 'The full strength offers the sweet flavour profile of mango with the slightly sour taste of the other ingredients.' },
            { value: 'Orange', png: 'orange', description: 'The characteristic orange citrus flavours make the flavour profile very similar to other sweetened orange drinks.' },
          ],
          rules: ['required'],
        },
        disclaimer: {
          title: 'Learn more',
          body: (
            <>
              <p>The flavour of your pre-workout will heavily depend on the other ingredients, and the ingredient profile generally pushes the taste on the sour side. All of the flavours have sweetners to balance the taste of the other ingredients.</p>
            </>
          ),
        },
      },
      {
        heading: 'How strong do you want the flavouring to be?',
        autoSubmit: true,
        field: {
          component: ButtonGroup,
          options: [
            {
              value: 'Standard',
              description: "The standard strength is very sweet, but still recommended if you want to mask the taste of the other ingredients as much as possible.",
              highlight: 'Recommended',
            },
            {
              value: 'Reduced',
              description: "The reduced strength is less sweet, but you will taste the other ingredients more. Depending on your formula, this may mean more bitterness or sourness.",
              highlight: 'Caution Is Advised',
            },
          ],
          cols: 2,
          extraClass: 'featured',
          rules: ['required'],
        },
      },
      {
        heading: 'Choose a colour for your pre-workout.',
        subHeading: 'Please note that the images below are meant as a guide only and the exact colour of your pre-workout may vary depending on its unique formula.',
        screen: ColourScreen,
        autoSubmit: false,
        field: {
          component: ColourGroup,
          options: [
            { value: 'No colouring', hexcode: null, img: 'supplements/no-colouring.webp' },
            { value: 'Yellow', hexcode: '#E7B545', img: 'supplements/yellow.webp' },
            { value: 'Orange', hexcode: '#E6A132', img: 'supplements/orange.webp' },
            { value: 'Peach', hexcode: '#BA744C', img: 'supplements/peach.webp' },
            { value: 'Red', hexcode: '#C83C3B', img: 'supplements/red.webp' },
            { value: 'Pink', hexcode: '#E2A0AE', img: 'supplements/pink.webp' },
            { value: 'Purple', hexcode: '#88516C', img: 'supplements/purple.webp' },
            { value: 'Light blue', hexcode: '#9CCCDF', img: 'supplements/light-blue.webp' },
            { value: 'Blue', hexcode: '#56A0DC', img: 'supplements/blue.webp' },
            { value: 'Teal', hexcode: '#459EB0', img: 'supplements/teal.webp' },
            { value: 'Green', hexcode: '#AED667', img: 'supplements/green.webp' },
          ],
          defaultValue: 'No colouring',
          extraClass: 'supplement-color',
          rules: ['required'],
        },
        disclaimer: {
          title: 'Learn more',
          body: (
            <>
              <p>The colour of your pre-workout may differ slightly depending on the ingredients in your formula, and can also differ depending on the screen where you are viewing the website. Some options are not available when certain ingredients are used.</p>
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Charity',
    steps: [
      {
        heading: 'Choose a charity.',
        subHeading: () => (
          <>
            We give 1% of our revenue to our charity partners and want your help deciding how it should be allocated. <br />To
            learn more about how this works, read our{' '}
            <a href="/charitable-donations-policy/" target="_blank" rel="noopener">
              <strong>Charitable Donations Policy</strong>
            </a>
          </>
        ),
        info: {
          title: null,
          description: 'Click on a charity above and learn more about it here.',
        },
        autoSubmit: false,
        field: {
          component: ImageGroup,
          extraClass: 'charity',
          options: [
            {
              value: 'Beyond Blue',
              svg: 'beyond-blue',
              info: {
                title: null,
                description:
                  "Beyond Blue is a safe and reliable place for millions of Australians to access information, advice and support for mental health, no matter whether they're well and want to stay that way, unwell and need support, or in recovery.",
              },
            },
            {
              value: 'Clean Up Australia Day',
              svg: 'clean-up-australia',
              info: {
                title: null,
                description:
                  "Clean Up Australia Day is our nation's largest, annual, community-based environmental event. Over three decades, Clean Up Australia has now become the country's most recognised, credible and trusted environmental charity, working towards practical solutions for our waste challenges to help us live more sustainably every day of the year.",
              },
            },
            {
              value: 'WIRES',
              svg: 'wires',
              info: {
                title: null,
                description:
                  "WIRES operate a dedicated Wildlife Rescue Office assisting wildlife and the community across Australia 24-hours a day, 365 days a year, providing rescue advice and assistance for over 130,000 animals annually. Caring for sick, injured and orphaned native animals for over 35 years, WIRES is Australia's largest wildlife rescue organisation.",
              },
            },
          ],
          rules: ['required'],
        },
      },
      {
        heading: 'Enter your email address to unlock your formula.',
        subHeading: 'By entering your email and pressing continue, you agree to our Privacy Policy and will be added to our mailing list.',
        autoSubmit: false,
        field: {
            component: Input,
            name: 'emailsubcribe',
            type: 'email',
            label: 'Enter your email address',
            rules: ['required', 'email'],
        },
      },
    ],
  },
];
